// Dependencies are pinned and served from the theme CDN; no third-party runtime requests.
class AspireViewer extends HTMLElement {
  connectedCallback() {
    if (this.abort) return;
    this.abort = new AbortController();
    this.dataset.ready = 'true';
    this.querySelector('[data-load]').addEventListener('click', () => this.load(), { signal: this.abort.signal });
  }

  async load() {
    if (this.loading) return;
    this.loading = true;
    const signal = this.abort.signal;
    const status = this.querySelector('[role="status"]');
    const start = this.querySelector('[data-load]');
    start.disabled = true;
    status.textContent = 'Loading the Aspire Board…';
    try {
      const [T, { OrbitControls }, { GLTFLoader }, { HDRLoader }, { mergeGeometries }, { createAspireMaterial }] = await Promise.all([
        import('./aspire-three.js'), import('./aspire-orbit.js'), import('./aspire-gltf.js'), import('./aspire-hdr.js'),
        import('./aspire-geometry-utils.js'), import('./aspire-materials.js'),
      ]);
      if (signal.aborted) return;
      const scene = new T.Scene();
      const renderer = new T.WebGLRenderer({ antialias: true, alpha: true, powerPreference: 'high-performance' });
      renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
      renderer.outputColorSpace = T.SRGBColorSpace;
      renderer.toneMapping = T.ACESFilmicToneMapping;
      renderer.toneMappingExposure = 1;
      renderer.shadowMap.enabled = true;
      renderer.shadowMap.type = T.VSMShadowMap;
      const stage = this.querySelector('[data-stage]');
      stage.append(renderer.domElement);
      renderer.domElement.setAttribute('aria-label', 'Interactive Aspire Board. Drag to rotate; scroll or pinch to zoom.');
      const camera = new T.PerspectiveCamera(36, 1, 0.01, 30);
      const controls = new OrbitControls(camera, renderer.domElement);
      controls.enableDamping = true;
      controls.dampingFactor = 0.08;
      controls.enablePan = false;
      controls.rotateSpeed = 0.65;
      controls.zoomSpeed = 0.65;
      controls.minPolarAngle = 0.01;
      controls.maxPolarAngle = Math.PI - 0.01;
      const resources = new Set();
      let visible = true;
      let frame = 0;
      this.cleanup = () => {
        cancelAnimationFrame(frame);
        resize.disconnect();
        intersection.disconnect();
        controls.dispose();
        resources.forEach(resource => resource.dispose());
        renderer.dispose();
        renderer.domElement.remove();
      };
      const resize = new ResizeObserver(() => {
        if (!modelReady) return;
        fit(false);
      });
      const intersection = new IntersectionObserver(entries => { visible = entries[0].isIntersecting; });
      intersection.observe(this);
      let modelReady = false;
      let radius = 1;
      let fittedDistance = 3;
      const home = new T.Vector3(0.15, 1.6, 0.95).normalize();
      const fit = (reset = true) => {
        const previousFit = fittedDistance;
        const width = Math.max(stage.clientWidth, 1), height = Math.max(stage.clientHeight, 1);
        camera.aspect = width / height;
        renderer.setSize(width, height, false);
        const vertical = T.MathUtils.degToRad(camera.fov / 2);
        const horizontal = Math.atan(Math.tan(vertical) * camera.aspect);
        fittedDistance = radius / Math.sin(Math.min(vertical, horizontal)) * 1.12;
        // The near plane remains outside the bounding sphere at every orbit angle.
        controls.minDistance = radius * 1.25 + camera.near;
        controls.maxDistance = fittedDistance * 1.65;
        camera.far = controls.maxDistance + radius * 8;
        if (reset) {
          controls.reset();
          controls.target.set(0, 0, 0);
          camera.position.copy(home).multiplyScalar(fittedDistance);
        } else camera.position.setLength(T.MathUtils.clamp(camera.position.length() * fittedDistance / previousFit, controls.minDistance, controls.maxDistance));
        camera.updateProjectionMatrix();
        controls.update();
      };
      const light = new T.DirectionalLight(0xffffff, 1.8);
      light.position.set(-2, 4, 3);
      light.castShadow = true;
      light.shadow.mapSize.set(2048, 2048);
      light.shadow.radius = 4;
      light.shadow.blurSamples = 8;
      Object.assign(light.shadow.camera, { left: -2, right: 2, top: 2, bottom: -2, near: 0.1, far: 12 });
      light.shadow.normalBias = 0.008;
      light.shadow.bias = -0.0001;
      scene.add(light, new T.HemisphereLight(0xeaf1ff, 0x7b8795, 1.1));
      resources.add({ dispose: () => light.shadow.dispose() });
      const gltf = await new GLTFLoader().loadAsync(this.dataset.model, event => {
        if (!signal.aborted && event.total) status.textContent = `Loading Aspire Board… ${Math.round(event.loaded / event.total * 100)}%`;
      });
      const model = gltf.scene;
      const materials = new Map();
      // Register resources even if the section was removed during the request.
      model.traverse(mesh => {
        if (!mesh.isMesh) return;
        resources.add(mesh.geometry);
        mesh.castShadow = mesh.receiveShadow = true;
        const tune = original => {
          let component = mesh;
          while (component.parent && !/^([JUDCR]\d+)$/.test(component.name)) component = component.parent;
          const key = `${original.uuid}:${/^J/.test(component.name)}`;
          if (materials.has(key)) return materials.get(key);
          resources.add(original);
          const material = createAspireMaterial(original, /^J/.test(component.name));
          resources.add(material);
          materials.set(key, material);
          return material;
        };
        // Shared material cache avoids thousands of duplicate CAD materials.
        mesh.material = Array.isArray(mesh.material) ? mesh.material.map(tune) : tune(mesh.material);
      });
      if (signal.aborted) { resources.forEach(resource => resource.dispose()); return; }
      // CAD exports contain thousands of tiny primitives. Batch static surfaces by
      // material to keep orbiting responsive without altering the source GLB.
      model.updateMatrixWorld(true);
      const batches = new Map();
      const originals = [];
      model.traverse(mesh => {
        if (!mesh.isMesh || Array.isArray(mesh.material)) return;
        const geometry = mesh.geometry.index ? mesh.geometry.toNonIndexed() : mesh.geometry.clone();
        geometry.applyMatrix4(mesh.matrixWorld);
        const signature = Object.entries(geometry.attributes).map(([name, a]) => `${name}:${a.itemSize}:${a.normalized}`).sort().join('|');
        const key = `${mesh.material.uuid}:${signature}`;
        if (!batches.has(key)) batches.set(key, { material: mesh.material, geometries: [] });
        batches.get(key).geometries.push(geometry);
        originals.push(mesh);
      });
      originals.forEach(mesh => mesh.removeFromParent());
      for (const { material, geometries } of batches.values()) {
        const geometry = mergeGeometries(geometries);
        geometries.forEach(item => item.dispose());
        if (!geometry) throw new Error('Incompatible Aspire CAD geometry');
        const mesh = new T.Mesh(geometry, material);
        mesh.castShadow = mesh.receiveShadow = true;
        model.add(mesh); resources.add(geometry);
      }
      const box = new T.Box3().setFromObject(model);
      const center = box.getCenter(new T.Vector3());
      const scale = 2 / Math.max(...box.getSize(new T.Vector3()).toArray());
      model.position.sub(center); // model root in this export is untransformed
      const pivot = new T.Group();
      pivot.add(model); pivot.scale.setScalar(scale); scene.add(pivot);
      radius = new T.Box3().setFromObject(pivot).getBoundingSphere(new T.Sphere()).radius;
      const groundGeometry = new T.PlaneGeometry(12, 12);
      const groundMaterial = new T.ShadowMaterial({ opacity: 0.16 });
      const ground = new T.Mesh(groundGeometry, groundMaterial);
      ground.rotation.x = -Math.PI / 2;
      ground.position.y = -radius - 0.12;
      ground.receiveShadow = true;
      scene.add(ground); resources.add(groundGeometry); resources.add(groundMaterial);
      try {
        const hdr = await new HDRLoader().loadAsync(this.dataset.environment);
        if (signal.aborted) { hdr.dispose(); resources.forEach(resource => resource.dispose()); return; }
        const pmrem = new T.PMREMGenerator(renderer);
        const environment = pmrem.fromEquirectangular(hdr);
        scene.environment = environment.texture;
        scene.environmentIntensity = 0.85;
        resources.add(environment); hdr.dispose(); pmrem.dispose();
      } catch (error) { console.warn('Aspire HDR unavailable; using studio lights.', error); }
      if (signal.aborted) return;
      modelReady = true;
      fit(); resize.observe(stage);
      const toolbar = this.querySelector('[data-controls]');
      toolbar.hidden = false;
      toolbar.addEventListener('click', event => {
        const action = event.target.closest('button')?.dataset.action;
        if (action === 'fit') fit();
        if (action === 'in' || action === 'out') {
          camera.position.setLength(T.MathUtils.clamp(camera.position.length() * (action === 'in' ? 0.85 : 1.18), controls.minDistance, controls.maxDistance));
        }
        if (action === 'left' || action === 'right') camera.position.applyAxisAngle(new T.Vector3(0,1,0), action === 'left' ? 0.3 : -0.3);
        if (action === 'back') { camera.position.copy(home).multiplyScalar(fittedDistance); camera.position.y *= -1; }
        controls.update();
      }, { signal });
      renderer.domElement.addEventListener('webglcontextlost', event => {
        event.preventDefault(); status.textContent = '3D graphics interrupted. Reload this page to restore the viewer.';
      }, { signal });
      start.hidden = true;
      status.textContent = 'Drag to rotate · Scroll or pinch to zoom';
      const animate = () => {
        frame = requestAnimationFrame(animate);
        if (visible && !document.hidden) { controls.update(); renderer.render(scene, camera); }
      };
      animate();
    } catch (error) {
      this.cleanup?.(); this.cleanup = null;
      if (signal.aborted) return;
      console.error('Aspire viewer:', error);
      status.textContent = 'The 3D view could not load. Check your connection and try again.';
      start.disabled = false; start.textContent = 'Retry 3D view'; this.loading = false;
    }
  }

  disconnectedCallback() {
    this.abort?.abort(); this.cleanup?.(); this.abort = null; this.cleanup = null; this.loading = false;
  }
}
if (!customElements.get('aspire-viewer')) customElements.define('aspire-viewer', AspireViewer);
